/**
 * @file: CustomerCommentPostedBody.tsx
 *
 * `OrgCustomerCommentPosted` — customer submitted a review. Shows
 * the overall score with a star row + a 4-cell grid of sub-scores +
 * the quote text behind an amber left rule.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgCustomerCommentPostedFeedPayload } from '@euler/model/feed';
import { FC } from 'react';
import { View } from 'react-native';
import { KeyValueGrid, TitleText, valueColor } from './common/primitives';

export const CustomerCommentPostedBody: FC<{
  p: OrgCustomerCommentPostedFeedPayload;
}> = ({ p }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View>
      <View
        css={`
          flex-direction: row;
          align-items: center;
        `}
      >
        <TitleText>{p.customerName ?? '车主'}的评价</TitleText>
        <Typography.Text ml={rpx(8)} size={rpx(14)} color="#f59e0b" bold>
          {(p.score ?? 0).toFixed(1)}
        </Typography.Text>
        <View style={{ marginLeft: rpx(6) }}>
          <ScoreStars score={p.score} />
        </View>
      </View>
      <KeyValueGrid
        items={[
          ['服务态度', `${p.serviceAttitudeScore}`],
          ['服务效率', `${p.serviceEfficiencyScore}`],
          ['专业程度', `${p.professionalDegreeScore}`],
          ['满意度', `${p.satisfactionDegreeScore}`],
        ]}
      />
      {p.comment ? (
        <View
          css={`
            margin-top: ${rpx(8)}px;
            padding-left: ${rpx(8)}px;
            border-left-width: ${rpx(2)}px;
            border-left-color: #f59e0b;
          `}
        >
          <Typography.Text
            size={rpx(13)}
            color={valueColor}
            lineHeight={rpx(20)}
          >
            {p.comment}
          </Typography.Text>
        </View>
      ) : null}
    </View>
  );
};

const ScoreStars: FC<{ score: number }> = ({ score }) => {
  const { rpx } = useResponsiveContext();
  const filled = Math.round(score);
  return (
    <View
      css={`
        flex-direction: row;
      `}
    >
      {[1, 2, 3, 4, 5].map(i => (
        <Typography.Text
          key={i}
          size={rpx(13)}
          color={i <= filled ? '#f59e0b' : '#e5e7eb'}
          style={{ marginRight: rpx(1) }}
        >
          ★
        </Typography.Text>
      ))}
    </View>
  );
};
